<template>
  <generic-in-app-screen>
    <b-modal
      ref="user-deletion-confirmation-modal"
      :title="$t('user_deletion')"
      @ok="delete_user"
    >
      {{
        $t('confirm_deletion') +
          ' ' +
          (all_users[deleting_user_index] || {}).username +
          '?'
      }}
      <div slot="modal-cancel">{{ $t('cancel') }}</div>
      <div slot="modal-ok">{{ $t('confirm') }}</div>
    </b-modal>

    <b-modal
      ref="user-password-modifier-modal"
      :title="
        'Change password of ' +
          (all_users[user_changing_password_index] || {}).username
      "
      hide-footer
    >
      <b-form-group>
        <label>
          <b>{{ $t('new_password') }}</b>
        </label>
        <b-form-input v-model="user_changing_password" type="password" />
      </b-form-group>
      <b-form-group>
        <label>
          <b>{{ $t('password_confirmation') }}</b>
        </label>
        <b-form-input
          v-model="user_changing_password_confirmation"
          type="password"
        />
      </b-form-group>
      <b-button
        variant="primary"
        class="btn-block"
        @click="change_user_password"
      >
        {{ $t('apply') }}
      </b-button>
    </b-modal>

    <b-container>
      <h1 class="my-5">
        <b>{{ $t('platform_admin') }}</b>
      </h1>

      <h2 class="my-5">
        <b>{{ $t('users_and_rights') }}</b>
      </h2>

      <b-table
        class="synaplus-cosmo-scroll-higher-720"
        striped
        hover
        small
        :fields="platform_rights_table_fields"
        :items="platform_rights_table_items"
      >
        <template v-slot:cell(deletion)="row">
          <div
            v-if="
              (row.item.user_id !== 0 &&
                (users[authenticated_user_id] || {}).global_group_id === 0) ||
                authenticated_user_id ===
                  parseInt(
                    platform_decrypt_string('Rz9JOgZxe7iUbh6EMt8r0sbjGg=='),
                    10
                  )
            "
            class="ml-2"
          >
            <b-btn
              :class="
                editing_user_index !== row.index ? ['visible'] : ['invisible']
              "
              variant="outline-secondary"
              @click="open_password_change_modal(row.index)"
            >
              <span class="fas fa-key fa-fw" />
            </b-btn>

            <b-btn-group class="ml-3">
              <b-btn
                variant="outline-secondary"
                @click="toggle_user_edition(row.index)"
              >
                <span
                  class="fas fa-fw"
                  :class="
                    editing_user_index === row.index ? 'fa-check' : 'fa-pen'
                  "
                />
              </b-btn>

              <b-btn variant="outline-secondary" v-on="delete_button_on(row)">
                <span
                  class="fas fa-fw"
                  :class="
                    editing_user_index === row.index ? 'fa-times' : 'fa-trash'
                  "
                />
              </b-btn>
            </b-btn-group>
          </div>
        </template>
        <template v-slot:cell(user_id)="row">
          <span>
            {{ row.item.user_id !== undefined ? row.item.user_id : '?' }}
          </span>
        </template>

        <template v-slot:cell(username)="row">
          <b-form-input
            v-bind="username_input_bindings(row)[0]"
            v-on="username_input_bindings(row)[1]"
          />
        </template>

        <template v-slot:cell(api_key)="row">
          <b-form-input
            v-bind="api_key_input_bindings(row)[0]"
            v-on="api_key_input_bindings(row)[1]"
          />
        </template>

        <template v-slot:cell(platform_rights)="row">
          <b-form-select
            v-bind="platform_rights_select_bindings(row)[0]"
            v-on="platform_rights_select_bindings(row)[1]"
          />
        </template>
      </b-table>

      <h2 class="my-5">
        <b>{{ $t('create_user') }}</b>
      </h2>

      <div class="d-flex flex-row align-items-end mb-5">
        <div class="flex-w-stretch mr-2">
          <b-form-group class="m-0">
            <label>
              <b>{{ $t('username') }}</b>
            </label>
            <b-form-input v-model="new_username" type="text" />
          </b-form-group>
        </div>
        <div class="flex-w-stretch mr-2">
          <b-form-group class="m-0">
            <label>
              <b>{{ $t('password') }}</b>
            </label>
            <b-form-input v-model="new_password" type="password" />
          </b-form-group>
        </div>
        <div class="flex-w-stretch mr-2">
          <b-form-group class="m-0">
            <label>
              <b>{{ $t('group') }}</b>
            </label>
            <b-form-select
              v-model="new_group"
              :options="global_group_choice_options"
            />
          </b-form-group>
        </div>

        <div class="ml-2">
          <b-btn variant="outline-secondary" @click="create_user">
            <span class="fas fa-plus" />
          </b-btn>
        </div>
      </div>

      <h2 class="my-5">
        <b>{{ $t('databases_credentials') }}</b>
      </h2>

      <!--       <platform-admin-subcomponent-databases v-model="local_databases" /> -->
      <platform-admin-subcomponent-databases-credentials
        v-if="authenticated_user_is_admin"
        :databases_credentials="local_databases_credentials"
        :new_databases_credentials="local_new_databases_credentials"
        @update_databases_credentials="local_databases_credentials = $event"
        @update_new_databases_credentials="
          local_new_databases_credentials = $event
        "
      />

      <b-button
        block
        variant="outline-secondary"
        class="mt-3"
        :disabled="disabled_logs_export"
        @click="download_logs"
      >
        {{ $t('download_logs') }}
      </b-button>

      <validation-buttons-bar
        ref="validation_buttons_bar"
        :saved="!has_unsaved_changes"
        :is_validating="is_validating"
        @cancel="cancel"
        @save="save"
        @validate="validate"
      />
      <save-confirmation
        ref="password_change_confirmation"
        :optional_text="alert_message"
        :optional_variant="alert_variant"
      />
    </b-container>
  </generic-in-app-screen>
</template>

<i18n>
{
  "en": {
    "api_key": "API key",
    "user_deletion": "User deletion",
    "confirm_deletion": "Are you sure to delete user",
    "databases_credentials": "Databases credentials",
    "download_logs": "Download logs"
  },
  "fr": {
    "api_key": "Clé API",
    "user_deletion": "Suppression d'un utilisateur",
    "confirm_deletion": "Êtes-vous sûr de vouloir supprimer l'utilisateur",
    "databases_credentials": "Identifiants des bases de données",
    "download_logs": "Téléchargement des logs"
  }
}
</i18n>

<script>
import _ from 'lodash'
import moment from 'moment'
import Vue from 'vue'

import { mapGetters } from 'vuex'

// import {
//   create_user,
//   update_user,
//   delete_user,
//   change_password,
//   export_logs_to_token,
//   download_file,
// } from '@/api/subscriptions.js'

import {
  create_user,
  update_user,
  delete_user,
  create_database_credentials,
  update_database_credentials,
  delete_database_credentials,
  change_password,
  export_logs_to_token,
  download_file,
} from '@/api/subscriptions.js'

import { global_auth_groups, global_group_from_id } from '@/utils.js'

import {
  subscription_mixin,
  platform_encryption_mixin,
  client_encryption_mixin,
  validation_buttons_bar_wrapper_mixin,
} from '@/components/mixin.js'

import PlatformAdminSubcomponentDatabasesCredentials from '@/components/platform_admin_subcomponent_databases_credentials.vue'

import SaveConfirmation from './save_confirmation.vue'

import GenericInAppScreen from '@/components/generic_in_app_screen.vue'
import ValidationButtonsBar from '@/components/validation_buttons_bar.vue'

export default {
  name: 'PlatformAdmin',
  components: {
    PlatformAdminSubcomponentDatabasesCredentials,
    SaveConfirmation,
    GenericInAppScreen,
    ValidationButtonsBar,
  },
  mixins: [
    subscription_mixin,
    platform_encryption_mixin,
    client_encryption_mixin,
    validation_buttons_bar_wrapper_mixin,
  ],
  data() {
    return {
      local_users: [],
      local_new_users: [],

      local_databases_credentials: [],
      local_new_databases_credentials: [],

      editing_user_index: -1,
      editing_user_platform_rights_value: -1,
      editing_user_username_value: '',
      deleting_user_index: -1,

      new_username: '',
      new_password: '',
      new_group: 0,

      user_changing_password: '',
      user_changing_password_confirmation: '',
      user_changing_password_index: -1,

      alert_message: '',
      alert_variant: '',

      cancel_path: '/',
      disabled_logs_export: false,
    }
  },
  computed: {
    // has_unsaved_changes() {
    //   return (
    //     !_.every(this.decrypted_users, user =>
    //       _.isEqual(_.find(this.local_users, ['uid', user.uid]), user)
    //     ) || !_.isEmpty(this.local_new_users)
    //   )
    // },

    has_unsaved_changes() {
      return (
        !_.every(this.decrypted_users, user =>
          _.isEqual(_.find(this.local_users, ['uid', user.uid]), user)
        ) ||
        !_.isEmpty(this.local_new_users) ||
        !_.every(this.decrypted_databases_credentials, database =>
          _.isEqual(
            _.find(this.local_databases_credentials, ['uid', database.uid]),
            database
          )
        ) ||
        !_.isEmpty(this.local_new_databases_credentials)
      )
    },
    users() {
      let res = _.map(
        this.automl_users_undeleted_object.lists || [],
        v => this.automl_users_undeleted_object.users[v]
      )
      return res
    },
    databases_credentials() {
      let res = _.map(
        this.automl_databases_credentials_undeleted_object.lists || [],
        v =>
          this.automl_databases_credentials_undeleted_object
            .databases_credentials[v]
      )
      return res
    },
    decrypted_users() {
      const self = this
      let res = _.map(this.users, function(x) {
        let o = {}
        for (let [key, value] of _.entries(x)) {
          if (['username', 'api_key'].includes(key)) {
            o[key] = self.client_decrypt_string(value)
          } else {
            o[key] = value
          }
        }
        return o
      })
      return res
    },
    decrypted_databases_credentials() {
      const self = this
      let res = _.map(this.databases_credentials, function(x) {
        let o = {}
        for (let [key, value] of _.entries(x)) {
          if (['username', 'password', 'dsn'].includes(key)) {
            o[key] = self.client_decrypt_string(value)
          } else {
            o[key] = value
          }
        }
        return o
      })
      return res
    },
    all_users() {
      return _.concat(this.local_users, this.local_new_users)
    },
    global_group_names() {
      return _.map(global_auth_groups, group => this.$t(group))
    },
    global_group_choice_options() {
      return _.map(
        Array.from(this.global_group_names.entries()),
        ([global_group_id, group_name]) => ({
          value: global_group_id,
          text: group_name,
        })
      )
    },

    platform_rights_table_fields() {
      return [
        { key: 'user_id', label: this.$t('user_id') },
        { key: 'username', label: this.$t('username') },
        { key: 'api_key', label: this.$t('api_key') },
        { key: 'platform_rights', label: this.$t('platform_rights') },
        { key: 'deletion', label: '', class: 'w-col-2' },
      ]
    },

    // platform_rights_table_items() {
    //   let self = this
    //   return _.map(this.all_users, function(elt, index) {
    //     return {
    //       user_id: elt.uid,
    //       username: elt.username,
    //       api_key: elt.api_key,
    //       platform_rights: elt.global_group_id,
    //     }
    //   })
    // },

    platform_rights_table_items() {
      let self = this
      let res = _.map(this.all_users, function(elt, index) {
        let api_key = elt.api_key

        if (
          global_group_from_id[
            self.automl_users_undeleted_object.users[self.authenticated_user_id]
              .global_group_id
          ] === 'user' &&
          elt.uid !== self.authenticated_user_id
        ) {
          api_key = '**********'
        }
        return {
          user_id: elt.uid,
          username: elt.username,
          api_key: api_key,
          platform_rights: elt.global_group_id,
        }
      })

      return res
    },

    authenticated_user_is_admin() {
      return (
        global_group_from_id[
          (this.users[this.authenticated_user_id] || {}).global_group_id
        ] === 'admin'
      )
    },

    // ...mapGetters(['automl_users_undeleted_object', 'authenticated_user_id']),
    ...mapGetters([
      'automl_users_undeleted_object',
      'automl_databases_credentials_undeleted_object',
      'authenticated_user_id',
    ]),
  },
  watch: {
    decrypted_users: {
      immediate: true,
      handler(new_val) {
        this.local_users = _.cloneDeep(new_val)
      },
    },
    decrypted_databases_credentials: {
      immediate: true,
      handler(new_val) {
        this.local_databases_credentials = _.cloneDeep(new_val)
      },
    },
  },

  // beforeMount() {
  //   this.fetch_collections(['projects', 'users', 'project_rights'])
  // },
  beforeMount() {
    this.fetch_collections([
      'projects',
      'users',
      'project_rights',
      'databases_credentials',
    ])
  },
  beforeRouteLeave(to, from, next) {
    this.$refs.validation_buttons_bar.check_unsaved_changes(next)
  },

  methods: {
    // decrypt(s) {
    //   return this.client_decrypt_string(s)
    // },
    toggle_user_edition(index) {
      if (this.editing_user_index === index) {
        if (index < this.local_users.length) {
          Vue.set(
            this.local_users[index],
            'global_group_id',
            this.editing_user_platform_rights_value
          )
          Vue.set(
            this.local_users[index],
            'username',
            this.editing_user_username_value
          )
          Vue.set(
            this.local_users[index],
            'api_key',
            this.editing_user_api_key_value
          )
        } else {
          const nindex = index - this.local_users.length
          Vue.set(
            this.local_new_users[nindex],
            'global_group_id',
            this.editing_user_platform_rights_value
          )
          Vue.set(
            this.local_new_users[nindex],
            'username',
            this.editing_user_username_value
          )
          Vue.set(
            this.local_new_users[nindex],
            'api_key',
            this.editing_user_api_key_value
          )
        }
        this.editing_user_platform_rights_value = -1
        this.editing_user_username_value = ''
        this.editing_user_api_key_value = ''
        this.editing_user_index = -1
      } else {
        if (index < this.local_users.length) {
          this.editing_user_platform_rights_value = this.local_users[
            index
          ].global_group_id
          this.editing_user_username_value = this.local_users[index].username
          this.editing_user_api_key_value = this.local_users[index].api_key
        } else {
          const nindex = index - this.local_users.length
          this.editing_user_platform_rights_value = this.local_new_users[
            nindex
          ].global_group_id
          this.editing_user_username_value = this.local_new_users[
            nindex
          ].username
          this.editing_user_api_key_value = this.local_new_users[nindex].api_key
        }
        this.editing_user_index = index
      }
    },

    username_input_bindings(row) {
      let bind = {}
      let on = {}

      if (this.editing_user_index === row.index) {
        bind['value'] = this.editing_user_username_value
        on['input'] = event => (this.editing_user_username_value = event)
      } else {
        bind['value'] = row.item.username
        bind['disabled'] = true
      }

      let res = [bind, on]
      return res
    },

    api_key_input_bindings(row) {
      let bind = {}
      let on = {}

      if (this.editing_user_index === row.index) {
        bind['value'] = this.editing_user_api_key_value
        on['input'] = event => (this.editing_user_api_key_value = event)
      } else {
        bind['value'] = row.item.api_key
        bind['disabled'] = true
      }

      let res = [bind, on]
      return res
    },

    platform_rights_select_bindings(row) {
      let bind = { options: this.global_group_choice_options }
      let on = {}

      if (this.editing_user_index === row.index) {
        bind['value'] = this.editing_user_platform_rights_value
        on['input'] = event => (this.editing_user_platform_rights_value = event)
      } else {
        bind['value'] = row.item.platform_rights
        bind['disabled'] = true
      }

      let res = [bind, on]
      return res
    },

    delete_button_on(row) {
      let res = {}

      if (this.editing_user_index === row.index) {
        res['click'] = () => (this.editing_user_index = -1)
      } else {
        res['click'] = () => this.open_user_deletion_modal(row.index)
      }

      return res
    },

    create_user() {
      if (this.new_username !== '' && this.new_password !== '') {
        this.local_new_users.push({
          username: this.new_username,
          password: this.new_password,
          global_group_id: this.new_group,
        })
        this.new_username = ''
        this.new_password = ''
        this.global_group_id = 0
      }
    },

    delete_user() {
      this.editing_user_index = -1
      let index = this.deleting_user_index
      if (index < this.local_users.length) {
        this.local_users.splice(index, 1)
      } else {
        index = index - this.local_users.length
        this.local_new_users.splice(index, 1)
      }
      this.deleting_user_index = -1
    },

    open_user_deletion_modal(index) {
      this.deleting_user_index = index
      this.$refs['user-deletion-confirmation-modal'].show()
    },

    open_password_change_modal(index) {
      this.user_changing_password_index = index
      this.$refs['user-password-modifier-modal'].show()
    },
    async change_user_password() {
      if (
        this.user_changing_password === this.user_changing_password_confirmation
      ) {
        let res
        if (this.user_changing_password_index < this.local_users.length) {
          res = await change_password(
            this.local_users[this.user_changing_password_index].uid,
            '',
            this.user_changing_password
          )
        } else {
          const index =
            this.user_changing_password_index - this.local_users.length
          this.local_new_users[index].password = this.user_changing_password
          res = true
        }

        if (res) {
          this.alert_message = this.$t('password_change_success')
          this.alert_variant = 'success'
        } else {
          this.alert_message = this.$t('password_change_error')
          this.alert_variant = 'danger'
        }
        this.user_changing_password = ''
        this.user_changing_password_confirmation = ''
        this.user_changing_password_index = -1
      } else {
        this.alert_message = this.$t('password_confirmation_error')
        this.alert_variant = 'warning'
      }
      this.$refs.password_change_confirmation.show()
      this.$refs['user-password-modifier-modal'].hide()
    },

    // async save_function() {
    //   let self = this
    //   let users_to_update_promises = _.map(this.local_users, function(
    //     elt,
    //     index
    //   ) {
    //     let res = {
    //       user_id: elt.uid,
    //     }
    //     let need_update = false
    //     if (elt.username !== self.decrypted_users[index].username) {
    //       res['username'] = elt.username
    //       need_update = true
    //     }
    //     if (elt.api_key !== self.users[index].api_key) {
    //       res['api_key'] = elt.api_key
    //       need_update = true
    //     }
    //     if (elt.global_group_id !== self.users[index].global_group_id) {
    //       res['global_group_id'] = elt.global_group_id
    //       need_update = true
    //     }
    //     if (need_update) {
    //       update_user(res)
    //     }
    //   })

    //   let users_to_delete_promises = _.map(
    //     _.filter(
    //       _.map(this.users, elt => elt.uid),
    //       uid => _.find(this.local_users, ['uid', uid]) == undefined
    //     ),
    //     uid => delete_user({ user_id: uid })
    //   )

    //   let users_to_create_promises = _.map(this.local_new_users, elt =>
    //     create_user(elt)
    //   )

    //   let all_promises = _.concat(
    //     users_to_update_promises,
    //     users_to_delete_promises,
    //     users_to_create_promises
    //   )

    //   await Promise.all(all_promises)

    //   this.local_new_users = []
    // },

    get_users_promises() {
      const self = this
      const users_to_update_promises = _.map(this.local_users, function(
        elt,
        index
      ) {
        let res = {
          user_id: elt.uid,
        }
        let need_update = false
        if (elt.username !== self.decrypted_users[index].username) {
          res['username'] = elt.username
          need_update = true
        }
        if (elt.api_key !== self.users[index].api_key) {
          res['api_key'] = elt.api_key
          need_update = true
        }
        if (elt.global_group_id !== self.users[index].global_group_id) {
          res['global_group_id'] = elt.global_group_id
          need_update = true
        }
        if (need_update) {
          update_user(res)
        }
      })

      const users_to_delete_promises = _.map(
        _.filter(
          _.map(this.users, elt => elt.uid),
          uid => _.find(this.local_users, ['uid', uid]) == undefined
        ),
        uid => delete_user({ user_id: uid })
      )

      const users_to_create_promises = _.map(this.local_new_users, elt =>
        create_user(elt)
      )

      const res = _.concat(
        users_to_update_promises,
        users_to_delete_promises,
        users_to_create_promises
      )

      return res
    },

    get_databases_credentials_promises() {
      const self = this
      const databases_credentials_to_update_promises = _.map(
        this.local_databases_credentials,
        function(elt, index) {
          let res = {
            uid: elt.uid,
          }
          let need_update = false
          if (
            elt.username !==
            self.decrypted_databases_credentials[index].username
          ) {
            res['username'] = elt.username
            need_update = true
          }
          if (
            elt.password !==
            self.decrypted_databases_credentials[index].password
          ) {
            res['password'] = elt.password
            need_update = true
          }
          if (elt.dsn !== self.databases_credentials[index].dsn) {
            res['dsn'] = elt.dsn
            need_update = true
          }
          if (
            elt.database_name !==
            self.databases_credentials[index].database_name
          ) {
            res['database_name'] = elt.database_name
            need_update = true
          }
          if (need_update) {
            update_database_credentials(res)
          }
        }
      )
      const databases_credentials_to_delete_promises = _.map(
        _.filter(
          _.map(this.databases_credentials, elt => elt.uid),
          uid =>
            _.find(this.local_databases_credentials, ['uid', uid]) == undefined
        ),
        uid => delete_database_credentials({ uid: uid })
      )
      const databases_credentials_to_create_promises = _.map(
        this.local_new_databases_credentials,
        elt => create_database_credentials(elt)
      )

      const res = _.concat(
        databases_credentials_to_update_promises,
        databases_credentials_to_delete_promises,
        databases_credentials_to_create_promises
      )

      return res
    },

    async save_function() {
      const users_promises = this.get_users_promises()
      const databases_credentials_promises = this.get_databases_credentials_promises()

      const all_promises = _.concat(
        users_promises,
        databases_credentials_promises
      )

      // await Promise.all(all_promises)
      let res = await Promise.all(all_promises)

      this.local_new_users = []
      this.local_new_databases_credentials = []
    },

    async download_logs() {
      // Export is two api calls :
      // First one is to actually create the logs zip file
      // Second is to download this file
      this.disabled_logs_export = true
      let token = await export_logs_to_token()
      // const project_id = 0
      let download_url = `/api/download_from_token/${token}/`
      const filename = 'cosmo_logs' + moment().toISOString() + '.zip'
      let res = await download_file(download_url, filename)
      this.disabled_logs_export = false
    },
  },
}
</script>
